import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Tag, Package, Edit } from 'lucide-react';
import { formatDateUK, formatCurrency, isDatePast } from '../utils/dateUtils';
import { handleApiError } from '../utils/errorHandler';
import api from '../utils/api';
import Layout from '../components/Layout';

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [statistics, setStatistics] = useState({
    totalBookings: 0,
    totalRevenue: 0,
    activeBookings: 0,
  });

  useEffect(() => {
    fetchCustomerDetails();
  }, [id]);

  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/customers/${id}/details`);
      setCustomer(response.data.customer);
      setBookings(response.data.bookings || []);
      setStatistics(response.data.statistics || {});
    } catch (error) {
      handleApiError(error, 'Failed to fetch customer details');
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-12 h-12 rounded-full border-b-2 border-blue-600 animate-spin"></div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="py-12 text-center">
        <p className="text-gray-500">Customer not found</p>
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center p-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/customers')}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{customer.name}</h1>
              <p className="mt-1 text-sm text-gray-500">Customer Details</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/customers', { state: { editCustomerId: id } })}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent shadow-sm hover:bg-blue-700"
          >
            <Edit className="mr-2 w-4 h-4" />
            Edit Customer
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 gap-6 p-4 md:grid-cols-3">
          <div className="p-6 bg-white rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 bg-blue-100 rounded-md">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Bookings</p>
                <p className="text-2xl font-semibold text-gray-900">{statistics.totalBookings}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="m-4 bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Customer Information</h2>
          </div>
          <div className="px-6 py-4 space-y-4">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-500">Customer Name</label>
                <p className="mt-1 text-sm text-gray-900">{customer.name}</p>
              </div>

              {customer.crmName && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">CRM Name</label>
                  <p className="mt-1 text-sm text-gray-900">{customer.crmName}</p>
                </div>
              )}

              {customer.crmId && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">CRM ID</label>
                  <p className="mt-1 text-sm text-gray-900">{customer.crmId}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-500">Business Types</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {customer.businessTypes && customer.businessTypes.length > 0 ? (
                    customer.businessTypes.map(bt => (
                      <span
                        key={bt._id}
                        className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium text-blue-800 bg-blue-100 rounded-full"
                      >
                        <Tag className="mr-1 w-3 h-3" />
                        {bt.section}
                      </span>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No business types assigned</p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 pt-4 border-t border-gray-200 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-500">Created</label>
                <p className="mt-1 text-sm text-gray-900">{formatDateUK(customer.createdAt)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Last Updated</label>
                <p className="mt-1 text-sm text-gray-900">{formatDateUK(customer.updatedAt)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Bookings */}
        <div className="m-4 bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Recent Bookings</h2>
          </div>
          <div className="overflow-x-auto">
            {bookings.length === 0 ? (
              <div className="py-12 text-center">
                <Package className="mx-auto w-12 h-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No bookings</h3>
                <p className="mt-1 text-sm text-gray-500">
                  This customer doesn't have any bookings yet.
                </p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Date
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Magazines
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      Total
                    </th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bookings.map(booking => (
                    <tr key={booking._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        {formatDateUK(booking.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="flex flex-wrap gap-1">
                          {(() => {
                            // Each flat Booking represents one magazine entry now.
                            const startIssue = booking.magazine?.schedule?.issues?.find(
                              i => i.name === booking.startIssue
                            );
                            const isClosed = startIssue && isDatePast(startIssue.closeDate);
                            return (
                              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-800 bg-gray-100 rounded">
                                <span className={isClosed ? 'italic text-red-600' : ''}>
                                  {booking.magazine?.name || 'Unknown'}
                                  {booking.startIssue && ` (${booking.startIssue})`}
                                </span>
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            booking.status === 'active' || booking.status === 'confirmed'
                              ? 'bg-green-100 text-green-800'
                              : booking.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {booking.status || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                        {formatCurrency(booking.totalPrice || 0)}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                        <button
                          onClick={() =>
                            navigate(
                              `/bookings/customer/${
                                booking.customer?._id || booking.customer || ''
                              }/view`
                            )
                          }
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CustomerDetail;
